import React from 'react';
import PropTypes from 'prop-types';
import styles from './SearchFilters.scss';
import MultipleSelect from '../../../shared/components/RCUIMultipleSelect';
import RCUISingleSelect from '../../../shared/components/RCUISingleSelect';
import { channelObjects } from '../../../shared/model/constants/channels';
import { CHANNEL_PROP_TYPES, PROPERTY_TYPE_OBJECT_PROP_TYPES } from '../../../shared/model/types';
import { propertyTypesForSelectElement } from '../../../shared/model/constants/propertyTypes';
import priceOptions from '../../searchResult/refinements/constants/priceOptions';
import pricePerAnnumOptions from '../../searchResult/refinements/constants/pricePerAnnumOptions';
import floorAreaOptions from '../../searchResult/refinements/constants/floorAreaOptions';

const { Leased, Invest } = channelObjects;

const FILTER_OPTION = PropTypes.shape({
  displayText: PropTypes.string.isRequired,
  value: PropTypes.number,
});

const FLOOR_FILTER_OPTION = PropTypes.shape({
  displayText: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});

// TODO: convert to stateless function
export default class SearchFilters extends React.Component {
  static propTypes = {
    currentChannel: CHANNEL_PROP_TYPES.isRequired,
    selectedPropertyTypes: PropTypes.arrayOf(PROPERTY_TYPE_OBJECT_PROP_TYPES),
    minPriceSelectedOptionForSaleSold: FILTER_OPTION.isRequired,
    minPriceSelectedOptionForLease: FILTER_OPTION.isRequired,
    maxPriceSelectedOptionForSaleSold: FILTER_OPTION.isRequired,
    maxPriceSelectedOptionForLease: FILTER_OPTION.isRequired,
    minFloorAreaSelectedOption: FLOOR_FILTER_OPTION.isRequired,
    maxFloorAreaSelectedOption: FLOOR_FILTER_OPTION.isRequired,
    onPropertyTypesChange: PropTypes.func.isRequired,
    onMinPriceChangeForSaleSold: PropTypes.func.isRequired,
    onMinPriceChangeForLease: PropTypes.func.isRequired,
    onMaxPriceChangeForSaleSold: PropTypes.func.isRequired,
    onMaxPriceChangeForLease: PropTypes.func.isRequired,
    onMinFloorAreaChange: PropTypes.func.isRequired,
    onMaxFloorAreaChange: PropTypes.func.isRequired,
  }

  minRenderer = (selectedOption) => {
    let content;
    if (selectedOption === pricePerAnnumOptions[0]) {
      content = 'Price p.a. (min)';
    } else if (selectedOption === priceOptions[0]) {
      content = 'Price (min)';
    } else {
      content = `Min: ${selectedOption.displayText}`;
    }
    return <span>{content}</span>;
  };

  maxRenderer = (selectedOption) => {
    let content;
    if (selectedOption === pricePerAnnumOptions[0]) {
      content = 'Price p.a. (max)';
    } else if (selectedOption === priceOptions[0]) {
      content = 'Price (max)';
    } else {
      content = `Max: ${selectedOption.displayText}`;
    }
    return <span>{content}</span>;
  };

  minFloorAreaRenderer = (selectedOption) => {
    const content = selectedOption === floorAreaOptions[0]
      ? 'Floor (min)'
      : `Min: ${selectedOption.displayText}`;
    return <span>{content}</span>;
  };

  maxFloorAreaRenderer = (selectedOption) => {
    const content = selectedOption === floorAreaOptions[0]
      ? 'Floor (max)'
      : `Max: ${selectedOption.displayText}`;
    return <span>{content}</span>;
  };

  isForSaleOrSold() {
    const { currentChannel } = this.props;

    return (
      currentChannel === channelObjects.ForSale ||
      currentChannel === channelObjects.Sold
    );
  }

  render() {
    const {
      currentChannel,
      selectedPropertyTypes,
      minPriceSelectedOptionForSaleSold,
      minPriceSelectedOptionForLease,
      maxPriceSelectedOptionForSaleSold,
      maxPriceSelectedOptionForLease,
      minFloorAreaSelectedOption,
      maxFloorAreaSelectedOption,
      onPropertyTypesChange,
      onMinPriceChangeForSaleSold,
      onMinPriceChangeForLease,
      onMaxPriceChangeForSaleSold,
      onMaxPriceChangeForLease,
      onMinFloorAreaChange,
      onMaxFloorAreaChange,
    } = this.props;

    const selectedPropertyTypeOptions = selectedPropertyTypes.map(propertyType => propertyType.id);

    return currentChannel !== Invest && (
      <div className={styles.filters}>
        <div className={styles.fieldGroup}>
          <MultipleSelect
            options={propertyTypesForSelectElement}
            selectedOptions={selectedPropertyTypeOptions}
            anyOptionValue="Any property type"
            onChange={onPropertyTypesChange}
            className={styles.propertyTypes}
          />
        </div>
        {currentChannel !== Leased &&
          <div className={styles.fieldGroup}>
            {this.isForSaleOrSold()
              ? <RCUISingleSelect
                options={priceOptions}
                placeholder="Price (min)"
                alignment="left"
                customRenderer={this.minRenderer}
                selectedOption={minPriceSelectedOptionForSaleSold}
                onChange={onMinPriceChangeForSaleSold}
                containerClassName={styles.singleSelectContainer}
                selectButtonClassName={styles.singleSelectButton}
                containerOpenClassName={styles.singleSelectOptionsListOpen}
                optionListClassName={styles.singleSelectOptionList}
                optionItemClassName={styles.singleSelectOptionItem}
                arrowUpClassName={styles.singleSelectArrowUp}
                arrowDownClassName={styles.singleSelectArrowDown}
              />
              : <RCUISingleSelect
                options={pricePerAnnumOptions}
                placeholder="Price (min)"
                alignment="left"
                customRenderer={this.minRenderer}
                selectedOption={minPriceSelectedOptionForLease}
                onChange={onMinPriceChangeForLease}
                containerClassName={styles.singleSelectContainer}
                selectButtonClassName={styles.singleSelectButton}
                containerOpenClassName={styles.singleSelectOptionsListOpen}
                optionListClassName={styles.singleSelectOptionList}
                optionItemClassName={styles.singleSelectOptionItem}
                arrowUpClassName={styles.singleSelectArrowUp}
                arrowDownClassName={styles.singleSelectArrowDown}
              />}
            {this.isForSaleOrSold()
              ? <RCUISingleSelect
                options={priceOptions}
                placeholder="Price (max)"
                alignment="right"
                customRenderer={this.maxRenderer}
                selectedOption={maxPriceSelectedOptionForSaleSold}
                onChange={onMaxPriceChangeForSaleSold}
                containerClassName={styles.singleSelectContainer}
                selectButtonClassName={styles.singleSelectButton}
                containerOpenClassName={styles.singleSelectOptionsListOpen}
                optionListClassName={styles.singleSelectOptionList}
                optionItemClassName={styles.singleSelectOptionItem}
                arrowUpClassName={styles.singleSelectArrowUp}
                arrowDownClassName={styles.singleSelectArrowDown}
              />
              : <RCUISingleSelect
                options={pricePerAnnumOptions}
                placeholder="Price (max)"
                alignment="right"
                customRenderer={this.maxRenderer}
                selectedOption={maxPriceSelectedOptionForLease}
                onChange={onMaxPriceChangeForLease}
                containerClassName={styles.singleSelectContainer}
                selectButtonClassName={styles.singleSelectButton}
                containerOpenClassName={styles.singleSelectOptionsListOpen}
                optionListClassName={styles.singleSelectOptionList}
                optionItemClassName={styles.singleSelectOptionItem}
                arrowUpClassName={styles.singleSelectArrowUp}
                arrowDownClassName={styles.singleSelectArrowDown}
              />
            }
          </div>
        }
        <div className={styles.fieldGroup}>
          <RCUISingleSelect
            options={floorAreaOptions}
            placeholder="Floor (min)"
            alignment="left"
            customRenderer={this.minFloorAreaRenderer}
            selectedOption={minFloorAreaSelectedOption}
            onChange={onMinFloorAreaChange}
            containerClassName={styles.singleSelectContainer}
            selectButtonClassName={styles.singleSelectButton}
            containerOpenClassName={styles.singleSelectOptionsListOpen}
            optionListClassName={styles.singleSelectOptionList}
            optionItemClassName={styles.singleSelectOptionItem}
            arrowUpClassName={styles.singleSelectArrowUp}
            arrowDownClassName={styles.singleSelectArrowDown}
          />
          <RCUISingleSelect
            options={floorAreaOptions}
            placeholder="Floor (max)"
            alignment="right"
            customRenderer={this.maxFloorAreaRenderer}
            selectedOption={maxFloorAreaSelectedOption}
            onChange={onMaxFloorAreaChange}
            containerClassName={styles.singleSelectContainer}
            selectButtonClassName={styles.singleSelectButton}
            containerOpenClassName={styles.singleSelectOptionsListOpen}
            optionListClassName={styles.singleSelectOptionList}
            optionItemClassName={styles.singleSelectOptionItem}
            arrowUpClassName={styles.singleSelectArrowUp}
            arrowDownClassName={styles.singleSelectArrowDown}
          />
        </div>
      </div>
    );
  }
}
